import { BigNumber } from 'ethers'
import { useEffect, useState } from 'react'
import { getVotingEscrowContract } from '~/contracts/getVotingEscrowContract'
import { useWallet } from '~/lib/useWallet'
import { CONFIG } from './config'
import { VESISConfig } from '~/types/config'
import { symbiosis } from '~/lib/symbiosis'

const VESIS_CONFIG = CONFIG.vesis

interface CreateVesisBalanceListenerParams {
	config: VESISConfig
	account: string
	onUpdate: (balance: BigNumber) => void
}

function createVesisBalanceListener({
	config,
	account,
	onUpdate,
}: CreateVesisBalanceListenerParams) {
	const provider = symbiosis.getProvider(config.chain.id)

	const contract = getVotingEscrowContract(config, provider)

	const updateBalance = () => {
		contract['balanceOf(address)'](account)
			.then((balance: BigNumber) => {
				onUpdate(balance)
			})
			.catch((error) => {
				onUpdate(BigNumber.from(0))
			})
	}

	const listener = () => {
		updateBalance()
	}

	// initial fetch
	updateBalance()

	provider.on('block', listener)

	return () => {
		provider.off('block', listener)
	}
}

export function useGetVeSisBalance(): BigNumber | null {
	const { account } = useWallet()

	const [balance, setBalance] = useState<BigNumber | null>(null)

	useEffect(() => {
		if (!account) {
			setBalance(null)
			return
		}

		let ignore = false

		const balances = new Map<string, BigNumber>()
		const watchers = VESIS_CONFIG.map((config) => {
			return createVesisBalanceListener({
				config,
				account,
				onUpdate: (balance) => {
					balances.set(config.slug, balance)

					if (balances.size !== VESIS_CONFIG.length) {
						// not all balances have been fetched yet
						return
					}

					if (ignore) {
						return
					}

					let total = BigNumber.from(0)
					for (const value of balances.values()) {
						total = total.add(value)
					}

					setBalance(total)
				},
			})
		})

		return () => {
			ignore = true
			watchers.forEach((unwatch) => unwatch())
		}
	}, [account])

	if (!account) {
		return BigNumber.from(0)
	}

	return balance
}
